import React from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const MultiSelectPopup = ({
  open,
  onClose,
  data,
  selectedItems,
  setSelectedItems,
  title,
}) => {
  // Handle item toggle for selection
  const handleToggle = (item) => {
    const updatedItems = selectedItems.includes(item)
      ? selectedItems.filter((i) => i !== item)
      : [...selectedItems, item];

    setSelectedItems(updatedItems);
  };

  const handleClearAll = () => {
    setSelectedItems([]);
  };

  // Handle "OK" button click
  const handleOk = () => {
    onClose();
  };

  // Handle close (X) - Clears selections on close
  const handleClose = () => {
    onClose();
  };

  const formatCamelCase = (text) => {
    return text.replace(/([a-z])([A-Z])/g, "$1 $2");
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing on outside click or escape key
        if (reason && reason !== "backdropClick") {
          onClose();
        }
      }}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          marginTop: "7vh",
          backgroundColor: "#000",
          boxShadow: "0px 0px 15px 5px rgba(255, 255, 255, 0.2)",
          borderRadius: "10px",
        },
      }}
    >
      {/* Title */}
      <DialogTitle
        sx={{
          color: "#fff",
          textAlign: "center",
          fontWeight: "bold",
          background: "linear-gradient(45deg, #ffa500, #ffffff)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          fontSize: "24px",
        }}
      >
        {title}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: "#ffa500",
            "&:hover": {
              color: "#d0892f",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent
        sx={{
          maxHeight: "600px",
          overflowY: "auto",
          padding: "16px",
          backgroundColor: "#000",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ffa500",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#d0892f",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#333",
          },
        }}
      >
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: "transparent",
            boxShadow: "none",
          }}
        >
          <Table>
            <TableBody>
              {data.map((item) => (
                <TableRow
                  key={item.key}
                  sx={{
                    "&:nth-of-type(odd)": { backgroundColor: "#222" },
                    "&:nth-of-type(even)": { backgroundColor: "#333" },
                  }}
                >
                  <TableCell
                    sx={{
                      color: "#e9a132",
                      fontWeight: "bold",
                      borderBottom: "1px solid #444",
                      verticalAlign: "top",
                    }}
                  >
                    <Checkbox
                      checked={selectedItems.includes(item.key)}
                      onChange={() => handleToggle(item.key)}
                      sx={{
                        color: "#ffa500",
                        "&.Mui-checked": { color: "#ffa500" },
                        paddingRight: "10px",
                      }}
                    />
                    {item.key}
                  </TableCell>

                  <TableCell
                    sx={{
                      color: "#aaa",
                      borderBottom: "1px solid #444",
                      verticalAlign: "top",
                      fontSize: "14px",
                      lineHeight: "1.5",
                    }}
                  >
                    {item.key === "Other" ? (
                      <></>
                    ) : (
                      Object.entries(item).map(
                        ([propName, propValue]) =>
                          propName !== "key" &&
                          propName !== "id" && (
                            <Box sx={{ mt: 1 }} key={propName}>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: "bold", color: "#ffa500" }}
                              >
                                {formatCamelCase(
                                  propName.charAt(0).toUpperCase() +
                                    propName.slice(1)
                                )}
                                :
                              </Typography>
                              <Typography variant="body2">
                                {propValue}
                              </Typography>
                            </Box>
                          )
                      )
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "16px",
          backgroundColor: "#000",
        }}
      >
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#ffa500",
            color: "white",
            "&:hover": { backgroundColor: "#d0892f" },
            padding: "10px 20px",
            fontWeight: "bold",
          }}
          onClick={handleOk}
        >
          OK
        </Button>
        <Button
          variant="outlined"
          sx={{
            color: "#ffa500",
            borderColor: "#ffa500",
            "&:hover": { borderColor: "#d0892f", color: "#d0892f" },
            padding: "10px 20px",
            fontWeight: "bold",
          }}
          onClick={handleClearAll}
        >
          Clear All
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MultiSelectPopup;
